import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { usePublisherData } from './usePublisherData';
import { useOfferPublisherSync } from './useOfferPublisherSync';
import { Loading } from '@/components/admin';
import { PageTransition } from '@/components/admin/PageTransition';

// Publisher context is URL-driven: the :publisherId param is the single source
// of truth. No react-admin store is written — this keeps multi-tab behaviour
// correct (each tab owns its own URL).
// We do write a plain localStorage hint so that non-publisher routes (Dashboard,
// NavigatePublisherIdPrefix) can do an immediate redirect without an API call.
export const PublisherLayout = () => {
  const { publisherId } = useParams<{ publisherId: string }>();
  const queryClient = useQueryClient();
  const { publishers, isLoading, error } = usePublisherData();
  useOfferPublisherSync(publishers, publisherId);

  const prevPublisherIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!publisherId) return;
    localStorage.setItem('gl:publisher_id', publisherId);
    if (
      prevPublisherIdRef.current &&
      prevPublisherIdRef.current !== publisherId
    ) {
      queryClient.invalidateQueries();
    }
    prevPublisherIdRef.current = publisherId;
  }, [publisherId, queryClient]);

  if (isLoading) return <Loading />;
  if (error) return <div>{`Error loading data.`}</div>;
  if (!publisherId && publishers.length === 0) {
    return <div>{`No publishers assigned to this account.`}</div>;
  }
  if (!publisherId) return <Loading />;

  return <PageTransition />;
};
